import React, { useEffect, useState } from 'react'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import {
  createAccessPass,
  getAccessPassEvents,
  getAccessPassTemplates,
} from '../../utilities/api'

const CUSTOM_TEMPLATE_VALUE = '__custom__'

export function CreateAccessPassModal({
  show,
  setShow,
  organizationUUID,
  onCreated,
  initialTemplateUUID,
  title = 'Create access pass',
  submitLabel = 'Create pass',
  submittingLabel = 'Creating...',
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [templates, setTemplates] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [events, setEvents] = useState([])
  const [selectedEventUUIDs, setSelectedEventUUIDs] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const lockTemplate = Boolean(initialTemplateUUID)
  const useCustomEvents =
    !lockTemplate && selectedTemplateId === CUSTOM_TEMPLATE_VALUE

  useEffect(() => {
    if (!show || !organizationUUID) return

    setError('')
    setLoading(true)
    setSelectedTemplateId('')
    setSelectedEventUUIDs([])
    setEmail('')
    setName('')

    Promise.all([
      getAccessPassTemplates(organizationUUID),
      getAccessPassEvents(organizationUUID),
    ])
      .then(([templatesRes, eventsRes]) => {
        const loadedTemplates = (templatesRes?.data?.data || []).filter(
          (template) => !template.disabled
        )
        setTemplates(loadedTemplates)
        setEvents(eventsRes?.data?.data || [])

        if (initialTemplateUUID) {
          const matched = loadedTemplates.find(
            (template) =>
              String(template.uuid || template.id) ===
              String(initialTemplateUUID)
          )
          if (matched) {
            setSelectedTemplateId(matched.uuid || matched.id)
            return
          }
        }

        if (loadedTemplates.length) {
          setSelectedTemplateId(
            loadedTemplates[0].uuid || loadedTemplates[0].id
          )
        } else {
          setSelectedTemplateId(CUSTOM_TEMPLATE_VALUE)
        }
      })
      .catch((err) => {
        console.error(err)
        setError('Could not load pass templates.')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [show, organizationUUID, initialTemplateUUID])

  const toggleEvent = (uuid) => {
    setSelectedEventUUIDs((current) =>
      current.includes(uuid)
        ? current.filter((id) => id !== uuid)
        : [...current, uuid]
    )
  }

  const selectedTemplate = templates.find(
    (template) =>
      String(template.uuid || template.id) === String(selectedTemplateId)
  )

  const passName = useCustomEvents ? name : selectedTemplate?.name || ''

  useEffect(() => {
    if (useCustomEvents) {
      setName('')
    }
  }, [useCustomEvents])

  const canSubmit =
    passName &&
    email &&
    (useCustomEvents ? selectedEventUUIDs.length > 0 : !!selectedTemplate)

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const payload = {
        name: passName,
        organizationUUID,
        email,
      }

      if (useCustomEvents) {
        payload.eventUUIDs = selectedEventUUIDs
      } else {
        payload.templateUUID = selectedTemplateId
      }

      await createAccessPass(payload)
      setShow(false)
      setName('')
      setEmail('')
      setSelectedEventUUIDs([])
      onCreated?.()
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Failed to create pass')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Stack gap={3}>
            <Form.Group controlId="accessPassTemplate">
              <Form.Label>Pass template</Form.Label>
              {loading ? (
                <p className="small text-secondary mb-0 mt-2">
                  Loading templates...
                </p>
              ) : lockTemplate && selectedTemplate ? (
                <p className="mb-0 mt-2">
                  {selectedTemplate.name} (
                  {selectedTemplate.events?.length || 0} events)
                </p>
              ) : (
                <Form.Select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  {templates.map((template) => (
                    <option
                      key={template.uuid || template.id}
                      value={template.uuid || template.id}
                    >
                      {template.name} ({template.events?.length || 0} events)
                    </option>
                  ))}
                  <option value={CUSTOM_TEMPLATE_VALUE}>
                    Custom event selection
                  </option>
                </Form.Select>
              )}
            </Form.Group>
            {useCustomEvents && (
              <Form.Group controlId="accessPassName">
                <Form.Label>Pass name</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. John Smith - Media Pass"
                  required
                />
              </Form.Group>
            )}
            <Form.Group controlId="accessPassEmail">
              <Form.Label>Assign to email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>
            {!useCustomEvents && selectedTemplate ? (
              <div>
                <Form.Label>Included events</Form.Label>
                <Stack gap={1} className="mt-2">
                  {(selectedTemplate.events || []).map((eventItem) => (
                    <p className="small mb-0" key={eventItem.uuid}>
                      {eventItem.name}
                      {eventItem.status ? ` (${eventItem.status})` : ''}
                    </p>
                  ))}
                </Stack>
              </div>
            ) : (
              <div>
                <Form.Label>Events</Form.Label>
                {loading ? (
                  <p className="small text-secondary mb-0 mt-2">
                    Loading events...
                  </p>
                ) : events.length === 0 ? (
                  <p className="small text-secondary mb-0 mt-2">
                    No eligible events found for this organization.
                  </p>
                ) : (
                  <Stack
                    gap={2}
                    className="mt-2"
                    style={{ maxHeight: 280, overflowY: 'auto' }}
                  >
                    {events.map((eventItem) => (
                      <Form.Check
                        key={eventItem.uuid}
                        type="checkbox"
                        id={`access-pass-event-${eventItem.uuid}`}
                        label={`${eventItem.name}${eventItem.status ? ` (${eventItem.status})` : ''}`}
                        checked={selectedEventUUIDs.includes(eventItem.uuid)}
                        onChange={() => toggleEvent(eventItem.uuid)}
                      />
                    ))}
                  </Stack>
                )}
              </div>
            )}
            {error && <p className="text-danger mb-0">{error}</p>}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || !canSubmit}>
            {submitting ? submittingLabel : submitLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
